import { FC, useContext, useState } from "react";
import {
  Box,
  CircularProgress,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { FormikHelpers, useFormik } from "formik";
import { Link } from "react-router-dom";
import * as yup from "yup";

import { Button, Image, OutlinedTextField } from "components";
import { AppContext } from "contexts";
import { overview } from "routes";
import { enableSMSNotifications } from "services/users";
import theme from "theme";

const useStyles = makeStyles(theme => ({
  container: {
    margin: "auto",
    "& > div:first-of-type": {
      marginBottom: theme.spacing(4),
    },
    "& > *": {
      marginBottom: theme.spacing(2),
    },
  },
  form: {
    "& > *": {
      marginBottom: theme.spacing(2),
    },
  },
}));

interface FormValues {
  phoneNumber: string;
}

const SMSNotificationsOptIn: FC = () => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AppContext);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    try {
      setError("");
      setSubmitting(true);
      await enableSMSNotifications({ phone_number: values.phoneNumber });
      setSuccess(true);
    } catch {
      setError(
        "Something went wrong while submitting your request. Please try again in a few minutes.",
      );
      return;
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: { phoneNumber: String(user.phone) ?? "" },
    validationSchema: yup
      .object()
      .shape({ phoneNumber: yup.string().required("Required") }),
    onSubmit: handleSubmit,
  });

  return (
    <Box className={classes.container}>
      {!!user ? (
        <>
          <Box width="100%">
            <Box width="150px" margin="auto">
              <Image name="re-logo.svg" />
            </Box>
          </Box>
          {!!error && <Alert severity="error">{error}</Alert>}
          {!success ? (
            <>
              <Typography color="primary">
                Confirm your phone number below to receive SMS notifications
                about your offers
              </Typography>
              <Box
                component="form"
                onSubmit={formik.handleSubmit}
                className={classes.form}
              >
                <OutlinedTextField
                  name="phoneNumber"
                  label="Phone Number"
                  value={formik.values.phoneNumber}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.phoneNumber && !!formik.errors.phoneNumber
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                  disabled={formik.isSubmitting}
                />
                <Box display="flex" justifyContent="flex-end" width="100%">
                  <Button
                    customColor="green"
                    variant="contained"
                    type="submit"
                    fullWidth={isXsDown}
                    disabled={formik.isSubmitting}
                    isLoading={formik.isSubmitting}
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography color="primary">
                Congratulations! You&apos;ll now receive text messages with news
                about your offers
              </Typography>
              <Box display="flex" justifyContent="center">
                <Button
                  customColor="green"
                  variant="contained"
                  component={Link}
                  to={overview()}
                >
                  Go to Listings
                </Button>
              </Box>
            </>
          )}
        </>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default SMSNotificationsOptIn;
